import { useEffect, useRef, useState } from "react";
import { ImageOff, Loader2 } from "lucide-react";

import { fetchFeedbackFrame } from "./echoFeedbackClient";

type AuthenticatedFeedbackImageProps = {
  reportId: string;
  alt: string;
  className?: string;
  eager?: boolean;
};

/** Render a private feedback frame without ever placing a Blob URL in markup. */
export function AuthenticatedFeedbackImage({
  reportId,
  alt,
  className = "h-full w-full object-contain",
  eager = false,
}: AuthenticatedFeedbackImageProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(eager);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (visible) return;
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "160px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const controller = new AbortController();
    let nextUrl: string | null = null;
    setObjectUrl(null);
    setFailed(false);
    fetchFeedbackFrame(reportId, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        nextUrl = URL.createObjectURL(blob);
        setObjectUrl(nextUrl);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => {
      controller.abort();
      if (nextUrl) URL.revokeObjectURL(nextUrl);
    };
  }, [reportId, visible]);

  return (
    <div
      ref={containerRef}
      className="flex h-full min-h-20 w-full items-center justify-center bg-black"
    >
      {objectUrl ? (
        <img src={objectUrl} alt={alt} className={className} />
      ) : failed ? (
        <span className="inline-flex items-center gap-1 text-xs text-white/70">
          <ImageOff className="size-4" /> Frame unavailable
        </span>
      ) : visible ? (
        <span className="inline-flex items-center gap-1 text-xs text-white/70">
          <Loader2 className="size-4 animate-spin" /> Loading private frame
        </span>
      ) : null}
    </div>
  );
}
